import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Dialog, Button } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { addResellerCustomer } from '@/modules/Reseller/services'
import CustomerSelect from '@/components/Form/Shared/CustomerSelect'
import { AddResellerCustomerRequest } from '@/modules/Reseller/types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface AddCustomerDialogProps {
    open: boolean
    resellerId: number
    onClose: () => void
    onSuccess: () => void
}

interface FormValues {
    customer_id: number | null
}

const AddCustomerDialog: React.FC<AddCustomerDialogProps> = ({
    open,
    resellerId,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation('reseller')

    const { control, handleSubmit, reset, formState } = useForm<FormValues>({
        defaultValues: {
            customer_id: null,
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: addResellerCustomer },
        {
            reactQueryKeys: QUERY_KEYS.RESELLER_CUSTOMERS,
            successMessage: t('customer_added_successfully'),
        }
    )

    const handleClose = () => {
        reset()
        onClose()
    }

    const onSubmit = async (data: FormValues) => {
        if (!data.customer_id) return

        const payload: AddResellerCustomerRequest = {
            customer_id: data.customer_id,
            status: 'active',
        }

        await mutateAsync({
            reseller_id: resellerId,
            payload,
        })

        reset()
        onSuccess()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={t('add_customer')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
                <CustomerSelect
                    control={control}
                    name="customer_id"
                    label={t('select_customer')}
                />

                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        disabled={isPending || !formState.isValid}
                    >
                        {isPending ? t('adding') : t('add')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default AddCustomerDialog
